import { useMemo } from 'react';
import { useModalContext } from './ModalProvider';
import { UseModalReturn } from './types';

/**
 * Hook to access modal functionality
 *
 * @example
 * ```typescript
 * const { open, confirm, closeAll } = useModal();
 *
 * // Open a modal with type safety
 * const result = await open(MyModalComponent, {
 *   requiredProp: 'value',
 *   optionalProp: 123
 * }, {
 *   modalProps: { title: 'My Modal', width: 600 }
 * });
 *
 * // Show confirmation dialog
 * const confirmed = await confirm({
 *   title: 'Are you sure?',
 *   content: 'This action cannot be undone.',
 * });
 *
 * // Close all modals
 * closeAll();
 * ```
 */
export function useModal(): UseModalReturn {
  const { open, openForm, confirm, closeAll } = useModalContext();

  // Memoize the return object to maintain referential stability
  return useMemo(
    () => ({
      open,
      openForm,
      confirm,
      closeAll,
    }),
    [open, openForm, confirm, closeAll]
  );
}
